import { auth, googleProvider } from '../config/firebase';
import { signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';

class AuthService {
  constructor() {
    this.user = null;
    this.authToken = null;
    this.isAuthenticated = false;
    this.isFirebaseEnabled = false;
    
    // Check if Firebase Auth is available
    this.checkFirebaseAuth();
  }

  async checkFirebaseAuth() {
    try {
      // Try to set up auth state listener
      onAuthStateChanged(auth, (user) => {
        this.user = user;
        this.isAuthenticated = !!user;
        if (user) {
          this.isFirebaseEnabled = true;
          console.log('🔐 User authenticated:', user.email);
          // Get the ID token
          user.getIdToken().then(token => {
            this.authToken = token;
          });
        } else {
          console.log('🔓 User signed out');
          this.authToken = null;
        }
      });
      this.isFirebaseEnabled = true;
    } catch (error) {
      console.warn('🚨 Firebase Auth not available:', error);
      this.isFirebaseEnabled = false;
    }
  }

  // Sign in with Google
  async signInWithGoogle() {
    if (!this.isFirebaseEnabled) {
      throw new Error('Firebase Authentication is not enabled. Please enable it in the Firebase console.');
    }
    
    try {
      const result = await signInWithPopup(auth, googleProvider);
      this.user = result.user;
      this.isAuthenticated = true;
      this.authToken = await result.user.getIdToken();
      console.log('✅ Successfully signed in:', result.user.email);
      return { success: true, user: result.user };
    } catch (error) {
      console.error('❌ Sign in failed:', error);
      throw error;
    }
  }

  // Get current auth token
  async getAuthToken() {
    if (!this.isAuthenticated || !this.user) {
      return null;
    }
    
    try {
      // Refresh token if needed
      this.authToken = await this.user.getIdToken(true);
      return this.authToken;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  // Make API request with Firebase Auth token (using apiFetch)
  async makeAPIRequest(url, options = {}) {
    // Import here to avoid circular dependency
    const { apiFetch } = await import('../utils/api');
    
    try {
      // Extract just the endpoint name from the URL
      let path = url;
      if (url.includes('/api/')) {
        // Extract everything after /api/
        path = url.substring(url.indexOf('/api/') + 5);
      }
      
      return await apiFetch(path, options);
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Make API call with proper error handling
  async callAPI(apiCall) {
    try {
      const result = await apiCall();
      return result;
    } catch (error) {
      console.error('API call failed:', error);
      
      // Provide user-friendly error message
      const errorMessage = this.getErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  getErrorMessage(error) {
    if (error.message?.includes('Authentication required')) {
      return 'Please sign in to access the application.';
    } else if (error.status === 401 || error.message?.includes('401') || error.message?.includes('Unauthorized')) {
      return 'Authentication expired. Please sign in again.';
    } else if (error.status === 403 || error.message?.includes('403')) {
      return 'Access denied. Please check your permissions.';
    } else if (error.message?.includes('Failed to fetch') || error.message?.includes('ERR_CONNECTION_TIMED_OUT')) {
      return 'Unable to connect to server. Please check your internet connection and try again.';
    } else {
      return 'API request failed. Please try again later.';
    }
  }

  // Ensure user is authenticated (sign in if needed)
  async ensureAuthenticated() {
    if (!this.isUserAuthenticated()) {
      console.log('🔐 User not authenticated, initiating sign-in...');
      await this.signInWithGoogle();
    }
    return this.isUserAuthenticated();
  }

  // Check if user is authenticated
  isUserAuthenticated() {
    return this.isAuthenticated;
  }

  // Check if Firebase is enabled
  isFirebaseAuthEnabled() {
    return this.isFirebaseEnabled;
  }

  // Get current user
  getCurrentUser() {
    return this.user;
  }

  // Sign out
  async signOut() {
    if (this.isFirebaseEnabled) {
      try {
        await signOut(auth);
      } catch (error) {
        console.error('Sign out error:', error);
      }
    }
    
    this.user = null;
    this.authToken = null;
    this.isAuthenticated = false;
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
